import { z } from 'zod';
import { SERVICE_SLUGS, BUDGET_RANGES } from '../constants/services.js';

export const LEAD_SOURCES = [
  'contact_page',
  'careers_page',
  'homepage_cta',
  'newsletter',
  'other',
] as const;
export const leadSourceEnum = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof leadSourceEnum>;

export const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'closed', 'spam'] as const;
export const leadStatusEnum = z.enum(LEAD_STATUSES);
export type LeadStatus = z.infer<typeof leadStatusEnum>;

/**
 * Public contact-form submission payload. Shared verbatim between the
 * client's react-hook-form resolver and the server's request validator so
 * the two can never drift out of sync.
 */
export const createLeadSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120),
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  phone: z
    .string()
    .trim()
    .regex(/^[+\d][\d\s()-]{6,19}$/, 'Enter a valid phone number')
    .optional()
    .or(z.literal('')),
  company: z.string().trim().max(160).optional().or(z.literal('')),
  serviceInterested: z.enum(SERVICE_SLUGS).optional(),
  budgetRange: z.enum(BUDGET_RANGES).optional(),
  message: z.string().trim().min(10, 'Tell us a bit more (min 10 characters)').max(2000),
  source: leadSourceEnum.default('contact_page'),
  /**
   * Honeypot field, hidden from real users via CSS. Deliberately accepts
   * any value here — rejecting a non-empty value with a 400 would let a
   * bot detect the trap from the validation error itself. The server
   * silently discards (rather than persists) any submission where this
   * came back non-empty; see `lead.service.ts`.
   */
  website: z.string().optional().default(''),
});

export type CreateLeadInput = z.infer<typeof createLeadSchema>;

export const updateLeadStatusSchema = z.object({
  status: leadStatusEnum,
});

export type UpdateLeadStatusInput = z.infer<typeof updateLeadStatusSchema>;

export const leadListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: leadStatusEnum.optional(),
  search: z.string().trim().max(200).optional(),
});

export type LeadListQuery = z.infer<typeof leadListQuerySchema>;
